/**
 * Browser-side request body for POST /api/xai/generate-image (Grok Image node).
 *
 * Pulled out of NodeAdapters.tsx so the payload shape is unit-testable. The
 * server route (server/routes/xai.ts) re-normalises quality/resolution before
 * reserving and forwarding, so this is not the billing authority — but it is
 * what the user's settings turn into, and it must never ask for 'auto'.
 */

import {
  GROK_IMAGE_2_MODEL_ID,
  GROK_IMAGE_2_MAX_INPUT_IMAGES,
  normalizeGrokImage2Tier,
} from './creditCostUtils';

/** Knods Grok Image node model ids that this builder handles. */
export const GROK_IMAGE_NODE_MODELS = new Set([
  'grok-image',
  'grok-image-pro',
  'grok-imagine-image-quality',
  GROK_IMAGE_2_MODEL_ID,
]);

/** Knods id → xAI API model id. */
export function grokImageApiModel(selectedModel: string): string {
  if (selectedModel === GROK_IMAGE_2_MODEL_ID) return GROK_IMAGE_2_MODEL_ID;
  if (selectedModel === 'grok-imagine-image-quality' || selectedModel === 'grok-image-pro') {
    return 'grok-imagine-image-quality';
  }
  return 'grok-imagine-image';
}

/**
 * Grok Image Quality / Pro are documented as text-to-image only, so the node
 * refuses reference images for them. Grok Image 2.0 supports editing with up
 * to 5 input images (docs.x.ai image editing guide names it; the API enforces
 * "at most 5 input image(s)").
 */
export function grokImageRejectsInputImages(selectedModel: string): boolean {
  return selectedModel === 'grok-image-pro' || selectedModel === 'grok-imagine-image-quality';
}

export interface GrokImageRequestInput {
  selectedModel: string;
  prompt: string;
  aspectRatio: string;
  /** node.data.parameters — only quality / resolution are read, and only for 2.0 */
  parameters?: Record<string, unknown>;
  inputImageUrls?: string[];
  flowId?: string | null;
}

export function buildGrokImageRequestBody(input: GrokImageRequestInput): Record<string, unknown> {
  const { selectedModel, prompt, aspectRatio, parameters = {}, inputImageUrls = [], flowId } = input;
  const body: Record<string, unknown> = {
    prompt,
    model: grokImageApiModel(selectedModel),
    n: 1,
    response_format: 'url',
    aspect_ratio: aspectRatio,
    flowId,
  };
  if (selectedModel === GROK_IMAGE_2_MODEL_ID) {
    // Always explicit: xAI's default 'auto' is billed at whatever it serves.
    const tier = normalizeGrokImage2Tier(parameters.quality, parameters.resolution);
    body.quality = tier.quality;
    body.resolution = tier.resolution;
  }
  const images = inputImageUrls.slice(0, GROK_IMAGE_2_MAX_INPUT_IMAGES);
  if (images.length === 1) body.image_url = images[0];
  else if (images.length > 1) body.image_urls = images;
  return body;
}
